import CloseIcon from '@mui/icons-material/Close';
import {
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  IconButton,
  InputBase,
  MenuItem,
  Select,
  type SelectChangeEvent,
  Typography,
} from '@mui/material';
import type React from 'react';
import type { ChangeEvent } from 'react';
import { insertItemAtIndex, isMuliValueFieldType, removeItemAtIndex } from '../../helpers';
import type { DateDisplayStyleCode, DatePrecisionCode, FieldTypeCode, MetadataFieldFormik } from '../../types';
import { availableFieldTypes, datePrecisionOptions, getStyleOptionsForPrecision } from '../const';
import { metadataFieldFormStyles } from './styles';

interface DisplayErrorProps {
  error: string;
}

const DisplayError: React.FC<DisplayErrorProps> = ({ error }) => {
  const classes = metadataFieldFormStyles();
  return (
    <div style={{ display: 'flex', justifyContent: 'center' }}>
      <Typography className={classes.fieldErrors}>{error}</Typography>
    </div>
  );
};

interface MetadataFieldFormProps {
  formik: MetadataFieldFormik;
}

const MetadataFieldForm: React.FC<MetadataFieldFormProps> = ({ formik }) => {
  const classes = metadataFieldFormStyles();

  const handleFieldTypeChange = (e: SelectChangeEvent<FieldTypeCode>): void => {
    const newFieldType = e.target.value;
    formik.setFieldValue('fieldType', newFieldType);
    if (newFieldType === 'DATE' && !formik.values.datePrecision) {
      formik.setFieldValue('datePrecision', 'DA');
    }
    formik.setFieldValue('dateDisplayStyle', 'iso');
  };

  const handleDatePrecisionChange = (e: SelectChangeEvent<DatePrecisionCode>): void => {
    formik.setFieldValue('datePrecision', e.target.value);
    formik.setFieldValue('dateDisplayStyle', 'iso');
  };

  const handleDateDisplayStyleChange = (e: SelectChangeEvent<string>): void => {
    formik.setFieldValue('dateDisplayStyle', e.target.value as DateDisplayStyleCode);
  };

  const handleChoiceChange = (index: number, e: ChangeEvent<HTMLInputElement>): void => {
    formik.setFieldValue('choices', insertItemAtIndex(formik.values.choices, index, e.target.value));
  };

  const handleRemoveChoice = (index: number): void => {
    formik.setFieldValue('choices', removeItemAtIndex(formik.values.choices, index));
  };

  const handleAddChoice = (): void => {
    formik.setFieldValue(
      'choices',
      insertItemAtIndex(formik.values.choices || [], formik.values.choices.length + 1, ''),
    );
  };

  return (
    <form onSubmit={formik.handleSubmit}>
      <div className={classes.main}>
        <div className={classes.formItem}>
          <div className={classes.labelColumn}>
            <Typography>Display Name</Typography>
          </div>
          <div className={classes.valueColumn}>
            <InputBase id="cyp-display-name-input" {...formik.getFieldProps('name')} className={classes.textInput} />
          </div>
        </div>
        {formik.errors?.name && <DisplayError error={'Please provide a Display Name'} />}
        <div className={classes.formItem}>
          <div className={classes.labelColumn}>
            <Typography>Slug</Typography>
          </div>
          <div className={classes.valueColumn}>
            <InputBase disabled readOnly {...formik.getFieldProps('slug')} className={classes.textInput} />
          </div>
        </div>

        <div className={classes.formItem}>
          <div className={classes.labelColumn}>
            <Typography>Field Type</Typography>
          </div>
          <div className={classes.valueColumn}>
            <FormControl fullWidth className={classes.formInput}>
              <Select
                id="cyp-field-type-select"
                label="Select Role"
                {...formik.getFieldProps('fieldType')}
                onChange={handleFieldTypeChange}
                value={formik.values.fieldType || 'TT'}
              >
                {availableFieldTypes.map(({ label, selectValue }) => (
                  <MenuItem key={selectValue} value={selectValue}>
                    {label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </div>
        </div>

        {formik.values.fieldType === 'DATE' && (
          <div className={classes.formItem}>
            <div className={classes.labelColumn}>
              <Typography>Date Precision</Typography>
            </div>
            <div className={classes.valueColumn} style={{ flexDirection: 'column', alignItems: 'flex-start' }}>
              <FormControl fullWidth className={classes.formInput}>
                <Select
                  id="cyp-date-precision-select"
                  value={formik.values.datePrecision || 'DA'}
                  onChange={handleDatePrecisionChange}
                >
                  {datePrecisionOptions.map(({ label, selectValue }) => (
                    <MenuItem key={selectValue} value={selectValue}>
                      {label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Typography variant="caption" sx={{ mt: 0.5, color: 'text.secondary' }}>
                {datePrecisionOptions.find((o) => o.selectValue === (formik.values.datePrecision || 'DA'))?.description}
              </Typography>
            </div>
          </div>
        )}

        {formik.values.fieldType === 'DATE' && formik.values.datePrecision !== 'YO' && (
          <div className={classes.formItem}>
            <div className={classes.labelColumn}>
              <Typography>Display Style</Typography>
            </div>
            <div className={classes.valueColumn}>
              <FormControl fullWidth className={classes.formInput}>
                <Select
                  id="cyp-date-style-select"
                  value={formik.values.dateDisplayStyle || 'iso'}
                  onChange={handleDateDisplayStyleChange}
                >
                  {(getStyleOptionsForPrecision(formik.values.datePrecision || 'DA') || []).map(
                    ({ label, selectValue, example }) => (
                      <MenuItem key={selectValue} value={selectValue}>
                        {label} — <em>{example}</em>
                      </MenuItem>
                    ),
                  )}
                </Select>
              </FormControl>
            </div>
          </div>
        )}

        {isMuliValueFieldType(formik.getFieldProps('fieldType')?.value as string) && (
          <div className={classes.formItemChoices}>
            <div></div>
            <div className={classes.choicesWrapper}>
              <Typography>Choices:</Typography>
              {formik.values.choices?.map((item, index) => (
                // biome-ignore lint/suspicious/noArrayIndexKey: choices are simple strings that can be duplicated
                <div key={`choice-${index}`} className={classes.choice}>
                  <InputBase
                    value={item}
                    id={`cyp-choices-${index}-input`}
                    onChange={(e: ChangeEvent<HTMLInputElement>) => handleChoiceChange(index, e)}
                    className={classes.textInput}
                  />
                  <button
                    type="button"
                    data-cyp="click-click-boom"
                    onClick={() => handleRemoveChoice(index)}
                    style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer' }}
                  >
                    <IconButton size="large">
                      <CloseIcon />
                    </IconButton>
                  </button>
                </div>
              ))}

              <div className={classes.addButton}>
                <Button id="cyp-add-choices-btn" onClick={handleAddChoice} variant="contained" color="primary">
                  Add
                </Button>
              </div>
            </div>
          </div>
        )}

        <div className={classes.formItem}>
          <div className={classes.labelColumn}>
            <Typography>Editable:</Typography>
          </div>
          <div className={classes.valueColumn}>
            <FormControlLabel
              label="User can edit the field"
              name="remember"
              className={classes.formCheckboxLabel}
              control={
                <Checkbox
                  id="cyp-isEditable-checkbox"
                  onChange={() => formik.setFieldValue('isEditable', !formik.getFieldProps('isEditable').value)}
                  checked={!!formik.getFieldProps('isEditable').value}
                  name="isEditable"
                />
              }
            />
          </div>
        </div>
        <div className={classes.formItem}>
          <div className={classes.labelColumn}>
            <Typography>Highlight If Blank:</Typography>
          </div>
          <div className={classes.valueColumn}>
            <FormControlLabel
              className={classes.formCheckboxLabel}
              label="Field will be highlighted if it's value is blank"
              name="remember"
              id="cyp-highlightIfBlank-checkbox"
              control={
                <Checkbox
                  onChange={() =>
                    formik.setFieldValue('highlightIfBlank', !formik.getFieldProps('highlightIfBlank').value)
                  }
                  checked={!!formik.getFieldProps('highlightIfBlank').value}
                />
              }
            />
          </div>
        </div>
        <div className={classes.formItem}>
          <div className={classes.labelColumn}>
            <Typography>Applies To:</Typography>
          </div>
          <div className={classes.valueColumn}>
            <div className={classes.formCheckboxWrapper}>
              <FormControlLabel
                className={classes.formCheckboxLabel}
                label="Assets"
                control={
                  <Checkbox
                    id="cyp-scope-assets-checkbox"
                    onChange={() => formik.setFieldValue('scopeAssets', !formik.getFieldProps('scopeAssets').value)}
                    checked={!!formik.getFieldProps('scopeAssets').value}
                  />
                }
              />
              <FormControlLabel
                className={classes.formCheckboxLabel}
                label="Galleries"
                control={
                  <Checkbox
                    id="cyp-scope-galleries-checkbox"
                    onChange={() =>
                      formik.setFieldValue('scopeGalleries', !formik.getFieldProps('scopeGalleries').value)
                    }
                    checked={!!formik.getFieldProps('scopeGalleries').value}
                  />
                }
              />
            </div>
          </div>
        </div>
      </div>
    </form>
  );
};

export default MetadataFieldForm;
